import React from "react";
import {
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button as FluentButton,
  Spinner,
  Menu,
  MenuItem,
  MenuTrigger,
  MenuPopover,
  MenuList,
} from "@fluentui/react-components";
import { ChevronDown12Regular, Checkmark12Regular } from "@fluentui/react-icons";
import Button from "../Button";
import ConfirmationForm from "../ConfirmationForm";
import ReportParameterField from "./ReportParameterField";
import ReportUdfSelector from "./ReportUdfSelector";
import { useReportParamsViewStyles } from "./ReportParamsViewStyles";
import { name } from "../../utils";
import {
  type Item,
  type ItemsState,
  type ParameterDefinition,
  type StringState,
  type UDFCategory,
} from "../ReportServices";

type ReportParamsViewProps = {
  reportName: string;
  sourceTemplateName: string;
  dataDefinition: ParameterDefinition;
  parameterDefinitions: ParameterDefinition[];
  dataDefinitions: ParameterDefinition[];
  selectedDataDefinitionIndex: number;
  items: ItemsState;
  udfs: Item[];
  udfCategories: UDFCategory[];
  selectedItem: StringState;
  selectedUdfs: string;
  areItemsLoading: Record<string, boolean>;
  areUdfsLoading: boolean;
  canGetData: boolean;
  canGetDatas?: boolean[];
  gotData: boolean;
  comboboxQuery: string;
  setComboboxQuery: (value: string) => void;
  udfComboboxQuery: string;
  setUdfComboboxQuery: (value: string) => void;
  comboboxOpen: string;
  setComboboxOpen: (value: string) => void;
  isComboboxOpen: boolean;
  setIsComboboxOpen: (value: boolean) => void;
  showLongRunningDataLoadNotice: boolean;
  isSaveConfirmationOpen: boolean;
  setIsSaveConfirmationOpen: (value: boolean) => void;
  saveCompleted: boolean;
  setSaveCompleted: (value: boolean) => void;
  isLoading: boolean;
  canCancel: boolean;
  error: string;
  setError: (value: string) => void;
  percent: number;
  isRefreshQueriesMessageShown: boolean;
  setIsRefreshQueriesMessageShown: (value: boolean) => void;
  enableDrilldownLinks: boolean;
  setEnableDrilldownLinks: (value: boolean) => void;
  setSelectedItem: (key: string, item: string) => void;
  setSelectedDataDefinitionIndex: (value: number, dataDefinitions: ParameterDefinition[]) => void;
  loadDependentDefData: (
    parameterDefinitions: ParameterDefinition[],
    dataDefinitions: ParameterDefinition[],
    selectedItem: StringState,
    newSelectedDataDefinitionIndex?: number | null
  ) => void;
  handleOptionSelectLocal: (nameId: string, data: any, targetItems: Item[]) => void;
  handleUdfCategoryToggle: (categoryName: string, isSelected: boolean) => void;
  handleUdfItemToggle: (itemId: string, isSelected: boolean) => void;
  handleMasterUdfToggle: (isSelected: boolean) => void;
  handleGetDataLocal: () => Promise<void>;
  handleGetData: (selectedDataDefinitionIndex: number) => Promise<void>;
  handleCancelDataLoad: () => void;
  handleSave: () => Promise<void>;
  handleSaveConfirmationClick: () => Promise<void>;
  setReportName: (value: string) => void;
  setOverWrite: (value: string) => void;
};

const ReportParamsView: React.FC<ReportParamsViewProps> = ({
  reportName,
  sourceTemplateName,
  dataDefinition,
  parameterDefinitions,
  dataDefinitions,
  selectedDataDefinitionIndex,
  items,
  udfs,
  udfCategories,
  selectedItem,
  selectedUdfs,
  areItemsLoading,
  areUdfsLoading,
  canGetData,
  canGetDatas,
  gotData,
  comboboxQuery,
  setComboboxQuery,
  udfComboboxQuery,
  setUdfComboboxQuery,
  comboboxOpen,
  setComboboxOpen,
  isComboboxOpen,
  setIsComboboxOpen,
  showLongRunningDataLoadNotice,
  isSaveConfirmationOpen,
  setIsSaveConfirmationOpen,
  saveCompleted,
  setSaveCompleted,
  isLoading,
  canCancel,
  error,
  setError,
  percent,
  isRefreshQueriesMessageShown,
  setIsRefreshQueriesMessageShown,
  enableDrilldownLinks,
  setEnableDrilldownLinks,
  setSelectedItem,
  setSelectedDataDefinitionIndex,
  loadDependentDefData,
  handleOptionSelectLocal,
  handleUdfCategoryToggle,
  handleUdfItemToggle,
  handleMasterUdfToggle,
  handleGetDataLocal,
  handleGetData,
  handleCancelDataLoad,
  handleSave,
  handleSaveConfirmationClick,
  setReportName,
  setOverWrite,
}) => {
  const styles = useReportParamsViewStyles();

  return (
    <div>
      <h3>
        {(reportName?.trim()?.length ?? 0) > 0
          ? name(reportName)
          : (sourceTemplateName?.trim()?.length ?? 0) > 0
            ? name(sourceTemplateName)
          : (dataDefinition.default?.length ?? 0) > 0
            ? name(dataDefinition.default)
            : "Analyzer"}
      </h3>
      <div>
        {parameterDefinitions.map((parameter) => (
          <ReportParameterField
            key={parameter.name}
            parameter={parameter}
            styles={styles}
            items={items}
            selectedItem={selectedItem}
            areItemsLoading={areItemsLoading}
            comboboxQuery={comboboxQuery}
            setComboboxQuery={setComboboxQuery}
            comboboxOpen={comboboxOpen}
            setComboboxOpen={setComboboxOpen}
            isComboboxOpen={isComboboxOpen}
            setIsComboboxOpen={setIsComboboxOpen}
            setSelectedItem={setSelectedItem}
            handleOptionSelectLocal={handleOptionSelectLocal}
          />
        ))}
      </div>

      <ReportUdfSelector
        styles={styles}
        visible={Boolean(dataDefinitions[selectedDataDefinitionIndex]?.call.endsWith("&udf=*"))}
        udfs={udfs}
        udfCategories={udfCategories}
        selectedUdfs={selectedUdfs}
        areUdfsLoading={areUdfsLoading}
        udfComboboxQuery={udfComboboxQuery}
        setUdfComboboxQuery={setUdfComboboxQuery}
        handleMasterUdfToggle={handleMasterUdfToggle}
        handleUdfCategoryToggle={handleUdfCategoryToggle}
        handleUdfItemToggle={handleUdfItemToggle}
      />

      <div className={styles.commandArea}>
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <Button
            label={"Get " + ((dataDefinitions[selectedDataDefinitionIndex]?.name?.length ?? 0) > 0 ? dataDefinitions[selectedDataDefinitionIndex].name : "Data")}
            onClick={handleGetDataLocal}
            className={dataDefinitions.length > 1 ? styles.button : styles.wideButton}
            disabled={!canGetData}
          />
          {dataDefinitions.length > 1 && (
            <Menu>
              <MenuTrigger>
                <FluentButton icon={<ChevronDown12Regular />} size="small" className={styles.dropDownButton} style={{ padding: "1px", margin: "0px", marginLeft: "-8px", zIndex: 1 }} />
              </MenuTrigger>
              <MenuPopover>
                <MenuList style={{ marginTop: "8px" }}>
                  {dataDefinitions.map((def, i) => (
                    <MenuItem
                      key={i}
                      onClick={() => {
                        setSelectedDataDefinitionIndex(i, dataDefinitions);
                        loadDependentDefData(parameterDefinitions, dataDefinitions, selectedItem, i);
                        handleGetData(i);
                      }}
                      disabled={canGetDatas && canGetDatas.length > i && !canGetDatas[i]}
                    >
                      {def.name?.length > 0 ? def.name : "Data"}
                      {i == selectedDataDefinitionIndex ? <Checkmark12Regular style={{ float: "right" }} /> : null}
                    </MenuItem>
                  ))}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </span>

        <ConfirmationForm
          isOpen={isRefreshQueriesMessageShown}
          onConfirm={() => {
            setIsRefreshQueriesMessageShown(false);
          }}
          dialogTitle="Refresh needed"
          dialogContent="The workbook contains queries that may need to be refreshed to reflect the new data. Please refresh the queries from the Data tab."
          confirmButtonLabel="OK"
        />
      </div>

      <div style={{ marginTop: "12px", marginBottom: "12px", paddingBottom: "12px", borderBottom: "0.25pt solid #E0E0E0" }}>
        <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={enableDrilldownLinks}
            onChange={(e) => setEnableDrilldownLinks(e.target.checked)}
            style={{ marginRight: "8px" }}
          />
          <span style={{ color: "#001C71" }}>Enable drill down links</span>
        </label>
      </div>

      <div className={styles.saveCommandArea}>
        <Input
          value={reportName || ""}
          onChange={(_, data) => setReportName(data.value)}
          placeholder="Analyzer name to save as"
          disabled={!gotData}
        />
        <Button label="Save" onClick={handleSave} className={styles.saveButton} disabled={!gotData || !reportName?.trim()?.length} />
        <ConfirmationForm
          isOpen={isSaveConfirmationOpen}
          onConfirm={() => {
            setIsSaveConfirmationOpen(false);
            setOverWrite("true");
            handleSaveConfirmationClick();
          }}
          onCancel={() => setIsSaveConfirmationOpen(false)}
          dialogTitle="Save analyzer"
          dialogContent="Are you sure you want to overwrite the existing analyzer on the server?"
          confirmButtonLabel="Overwrite"
          cancelButtonLabel="Cancel"
        />
        <Dialog open={saveCompleted}>
          <DialogSurface style={{ margin: "16px" }}>
            <DialogBody>
              <DialogTitle>Save</DialogTitle>
              <DialogContent>
                <p>The analyzer has been saved successfully.</p>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button onClick={() => setSaveCompleted(false)} label="Close" />
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
        {isLoading && (
          <div className={styles.spinnerContainer}>
            <div className={styles.loadingOverlayContent}>
              <div className={styles.loadingStatus}>
                <Spinner size="large" />
                {percent > 0 && <span className={styles.loadingText}>Loading... {`${percent.toFixed(0)}%`}</span>}
              </div>
              {canCancel && (
                <div className={styles.loadingActions}>
                <Button label="Cancel" onClick={handleCancelDataLoad} className={styles.cancelButton} />
                </div>
              )}
              {showLongRunningDataLoadNotice && (
                <div className={styles.longRunningDataLoadNotice} role="status" aria-live="polite">
                  <div className={styles.longRunningDataLoadNoticeTitle}>This query is taking longer than usual.</div>
                  <p className={styles.longRunningDataLoadNoticeText}>
                    You can keep waiting, or cancel and try again with fewer selected items, fewer UDFs, or more specific filter values.
                  </p>
                </div>
              )}
              </div>
          </div>
        )}
        <Dialog open={(error?.length ?? 0) > 0}>
          <DialogSurface style={{ margin: "16px" }}>
            <DialogBody>
              <DialogTitle>Error</DialogTitle>
              <DialogContent>
                <p style={{ color: "red" }}>
                  An error has occurred:
                  <br />
                  {error}
                </p>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button onClick={() => setError("")} label="Close" />
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      </div>
    </div>
  );
};

export default ReportParamsView;
